/**
 * The order a member-timeline item links to (DNKADM-0473, contract DNKAPI-0687).
 *
 * A COMP, ORDER_VOID or order payment item carries `links.orderId` + `links.orderNumber`.
 * There is no order route in the console, so "View order" opens this read-only summary of
 * `GET /v2/admin/members/{contactId}/orders/{orderId}` (`getMemberOrder`, the same read the
 * sell and membership dialogs settle on): number, status, lines and totals. It offers no
 * action; refund and void stay on the payments ledger.
 */
import { useCallback } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import { getMemberOrder } from '@/api/membershipOrders';
import { OrderStatusRow } from '@/components/OrderStatusBadge';
import { ScreenError } from '@/components/ScreenError';
import { useResource } from '@/hooks/useResource';
import { MONO } from '@/theme';
import { formatMoney } from '@/utils/v2Format';
import { scrubBrand } from '@/utils/whitelabel';
import { TIMELINE_COPY } from './memberTimelineCopy';

/** The order wire has no currency on older rows; the console's shared fallback. */
const FALLBACK_CURRENCY = 'USD';

export function TimelineOrderDialog({
  contactId,
  orderId,
  orderNumber,
  onClose,
}: {
  contactId: string;
  orderId: number;
  /** The timeline's receipt number, shown in the title before the read lands. */
  orderNumber: string | null;
  onClose: () => void;
}) {
  const order = useResource(
    useCallback(() => getMemberOrder(contactId, orderId), [contactId, orderId]),
    [contactId, orderId],
  );
  const label = order.data?.orderNumber ?? orderNumber ?? String(orderId);
  const currency = order.data?.currency ?? FALLBACK_CURRENCY;

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>{`${TIMELINE_COPY.orderTitle} ${label}`}</DialogTitle>
      <DialogContent>
        {order.status === 'loading' && order.data === null && (
          <Stack
            direction="row"
            role="status"
            aria-label={TIMELINE_COPY.orderLoading}
            sx={{ alignItems: 'center', gap: 1.5, py: 3 }}
          >
            <CircularProgress size={20} />
            <Typography variant="body2">{TIMELINE_COPY.orderLoading}</Typography>
          </Stack>
        )}
        {order.status === 'error' && (
          <ScreenError
            error={order.error}
            what="the order"
            onRetry={order.refetch}
            retryMessage={TIMELINE_COPY.orderError}
            preferServerMessage
          />
        )}
        {order.data !== null && order.status !== 'error' && (
          <Stack spacing={1} data-testid="timeline-order">
            <OrderStatusRow status={order.data.status} />
            <Divider />
            {order.data.lines.map((line, index) => (
              <MoneyLine
                key={`${line.type ?? 'line'}-${line.refId ?? index}`}
                label={scrubBrand(line.tier) ?? TIMELINE_COPY.orderLineFallback}
                value={formatMoney(line.total, currency)}
              />
            ))}
            {order.data.lines.length > 0 && <Divider />}
            <MoneyLine label="Subtotal" value={formatMoney(order.data.subtotal, currency)} />
            <MoneyLine label="Tax" value={formatMoney(order.data.taxTotal, currency)} />
            <MoneyLine label="Total" value={formatMoney(order.data.grandTotal, currency)} strong />
            {order.data.compValue !== null && (
              <MoneyLine label="Comp value" value={formatMoney(order.data.compValue, currency)} />
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{TIMELINE_COPY.orderClose}</Button>
      </DialogActions>
    </Dialog>
  );
}

function MoneyLine({ label, value, strong = false }: { label: string; value: string; strong?: boolean }) {
  return (
    <Stack direction="row" sx={{ justifyContent: 'space-between', gap: 1 }}>
      <Typography sx={{ fontSize: 13, fontWeight: strong ? 600 : 400 }}>{label}</Typography>
      <Box component="span" sx={{ fontFamily: MONO, fontSize: 13, fontWeight: strong ? 600 : 400 }}>
        {value}
      </Box>
    </Stack>
  );
}
